/**
 * Widget analytics — lightweight event beacon.
 *
 * Design goals:
 *   - Zero render impact: events buffer in memory, flush via navigator.sendBeacon
 *     on idle / unload / batch threshold
 *   - Persistent visitor_id (localStorage), session_id (sessionStorage)
 *   - No PII: only anonymized random UUIDs, no IP storage on our side
 *   - Respects Do Not Track and prefers-reduced-data
 *   - Skips entirely if widget-config returned a non-free tier and the
 *     site owner has opted out (future flag)
 */

const VISITOR_KEY = 'nagisot.aid';   // persistent visitor anon id
const SESSION_KEY = 'nagisot.sid';   // sessionStorage session id
const FLUSH_AFTER_MS = 4_000;        // flush 4s after last event
const FLUSH_BATCH = 12;              // flush when we reach 12 events
const MAX_QUEUE = 60;                // hard cap (drops oldest if exceeded)

type EventType =
  | 'widget.boot'
  | 'widget.banner_shown' | 'widget.banner_close' | 'widget.banner_cta'
  | 'panel.open' | 'panel.close' | 'panel.help' | 'panel.cta'
  | 'feature.toggle' | 'feature.reset'
  | 'fab.moved' | 'fab.reset_position'
  | 'lang.change'
  | 'tts.start' | 'tts.stop'
  | 'read_mask.toggle';

interface QueuedEvent {
  type: EventType;
  ts: number;
  payload?: Record<string, unknown>;
}

let cfg: { siteId: string; apiOrigin: string } | null = null;
let queue: QueuedEvent[] = [];
let flushTimer: number | null = null;
let visitorId = '';
let sessionId = '';
let ctx: { url: string; title: string; referrer: string; ua: string; lang: string } | null = null;

/** Generate a short UUID-ish (lowercase hex, 24 chars). */
function rid(): string {
  const buf = crypto.getRandomValues(new Uint8Array(12));
  let s = '';
  for (const b of buf) s += b.toString(16).padStart(2, '0');
  return s;
}

function loadOrCreateVisitor(): string {
  try {
    const existing = localStorage.getItem(VISITOR_KEY);
    if (existing && /^v_[0-9a-f]+$/.test(existing)) return existing;
    const fresh = 'v_' + rid();
    localStorage.setItem(VISITOR_KEY, fresh);
    return fresh;
  } catch {
    // privacy mode etc — use ephemeral
    return 'v_' + rid();
  }
}

function loadOrCreateSession(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_KEY);
    if (existing && /^s_[0-9a-f]+$/.test(existing)) return existing;
    const fresh = 's_' + rid();
    sessionStorage.setItem(SESSION_KEY, fresh);
    return fresh;
  } catch {
    return 's_' + rid();
  }
}

/** Honor browser-level opt-outs. */
function shouldTrack(): boolean {
  if (typeof navigator === 'undefined') return false;
  // Respect DNT (older but still used by some users)
  // and Global Privacy Control (newer, GDPR-relevant)
  const nav = navigator as Navigator & { doNotTrack?: string; globalPrivacyControl?: boolean };
  if (nav.doNotTrack === '1' || (typeof window !== 'undefined' && (window as { doNotTrack?: string }).doNotTrack === '1')) return false;
  if (nav.globalPrivacyControl === true) return false;
  return true;
}

/** Strip query + hash from current URL for tidy analytics. */
function canonicalUrl(): string {
  try {
    const u = new URL(location.href);
    return u.origin + u.pathname.replace(/\/$/, '') || '/';
  } catch {
    return location.pathname || '/';
  }
}

/**
 * Initialize the analytics module. Safe to call multiple times.
 * Pass siteId from the loader's data-site, and apiOrigin from config.
 */
export function initAnalytics(params: { siteId: string; apiOrigin: string }): void {
  if (!shouldTrack()) return;
  if (cfg) return; // already initialized

  cfg = { siteId: params.siteId, apiOrigin: params.apiOrigin };
  visitorId = loadOrCreateVisitor();
  sessionId = loadOrCreateSession();
  ctx = {
    url: canonicalUrl(),
    title: (document.title || '').slice(0, 256),
    referrer: (document.referrer || '').slice(0, 512),
    ua: navigator.userAgent || '',
    lang: (document.documentElement.lang || navigator.language || 'en').slice(0, 8),
  };

  // Flush queued events on page hide / unload — most important moment
  // because that's when users navigate away after engaging with the panel.
  const onLeave = (): void => { void flush(true); };
  window.addEventListener('pagehide', onLeave);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flush(true);
  });

  // First heartbeat — confirms the widget loaded
  track('widget.boot');
}

/** Track an event. Buffered, then flushed asynchronously. */
export function track(type: EventType, payload?: Record<string, unknown>): void {
  if (!cfg) return;
  queue.push({ type, ts: Date.now(), payload });
  if (queue.length > MAX_QUEUE) queue.splice(0, queue.length - MAX_QUEUE);
  if (queue.length >= FLUSH_BATCH) {
    void flush(false);
    return;
  }
  if (flushTimer) window.clearTimeout(flushTimer);
  flushTimer = window.setTimeout(() => { void flush(false); }, FLUSH_AFTER_MS);
}

/** Send all queued events to the API. `useBeacon` for unload paths. */
async function flush(useBeacon: boolean): Promise<void> {
  if (!cfg || queue.length === 0) return;
  const batch = queue.splice(0, queue.length);
  if (flushTimer) { window.clearTimeout(flushTimer); flushTimer = null; }

  const body = JSON.stringify({
    siteId: cfg.siteId,
    visitorId,
    sessionId,
    events: batch,
    ctx,
  });
  const url = `${cfg.apiOrigin}/public/widget-event`;
  try {
    if (useBeacon && navigator.sendBeacon) {
      // sendBeacon needs a Blob with the right content-type for some browsers
      const blob = new Blob([body], { type: 'application/json' });
      const sent = navigator.sendBeacon(url, blob);
      if (sent) return;
    }
    // fall through to fetch
    await fetch(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body,
      keepalive: true,
      credentials: 'omit',
      mode: 'cors',
    });
  } catch {
    // If flush failed, restore queue so we retry on next event (cap protects us)
    queue.unshift(...batch.slice(0, MAX_QUEUE - queue.length));
  }
}

/** Public helper for direct calls from widget UI handlers (panel.ts uses this). */
export const analytics = { track };
